import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
} from '@/components/v4';
import {
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    VStack,
    HStack,
    Box,
    Text,
    IconButton,
    Tooltip,
} from '@chakra-ui/react';
import {
    SearchIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    CloseIcon,
} from '@chakra-ui/icons';
import { useSelector } from 'react-redux';
import { getNavigationData, getCachedNavigationData } from './navigationCache';
import { getPickerColors, getModalColors } from './pickerColors';

/**
 * Reusable Partner/Company Picker Modal
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelect - Callback when selection is made. Receives { partnerId, partnerName, companyId, companyName }
 * @param {string} currentPartnerId - Currently selected partner ID (for smart ordering)
 * @param {string} currentCompanyId - Currently selected company ID (for smart ordering)
 * @param {string} mode - 'partner' or 'company' - determines what gets cleared/selected
 * @param {string} title - Modal title (default: "Select Partner/Company")
 * @param {string} filterByPartnerId - DEPRECATED: No longer filters - shows all partners
 * @param {string} wholesalerId - Wholesaler ID to fetch data for (overrides Redux currentWholesaler)
 * @param {string} managedCompanyId - Currently managed company ID (for ordering - second priority after selected)
 * @param {string} managedPartnerId - Currently managed partner ID (for ordering - second priority after selected)
 */
const PartnerCompanyPicker = ({
    isOpen,
    onClose,
    onSelect,
    currentPartnerId = null,
    currentCompanyId = null,
    // eslint-disable-next-line no-unused-vars
    mode = 'company', // 'partner' or 'company'
    title = 'Select Partner / Company',
    // eslint-disable-next-line no-unused-vars
    filterByPartnerId = null, // DEPRECATED: No longer used - always show all partners
    wholesalerId = null, // New prop for wholesaler ID
    scope = null, // User scope (40 = partner, 60+ = wholesaler/global) - if null, get from Redux
    managedCompanyId = null, // Currently managed company (for ordering)
    managedPartnerId = null, // Currently managed partner (for ordering)
}) => {
    const { currentPartner, requiredScope } = useSelector(
        (state) => ({
            currentPartner: state.navigationLists.currentPartner,
            requiredScope: state.login.requiredScope,
        }),
    );
    const { darkMode } = useSelector((state) => state.settings);

    // Use provided wholesalerId ONLY - don't fall back to Redux to avoid stale data
    const activeWholesaler = wholesalerId || null;
    // Use provided scope or fall back to Redux requiredScope - normalize 80 to 60
    const rawScope = scope || requiredScope || 60;
    const activeScope = rawScope === 40 ? 40 : 60; // Only 40 or 60, never 80
    // For scope 40, use currentPartnerId or currentPartner from Redux
    const activePartnerId = currentPartnerId || currentPartner;

    // Load PROCESSED data from cache immediately (instant!)
    const cachedData = useMemo(
        () =>
            getCachedNavigationData(
                activeWholesaler,
                activeScope,
                activePartnerId,
            ),
        [activeWholesaler, activeScope, activePartnerId],
    );

    // Initialize states with cached data if available
    const [searchTerm, setSearchTerm] = useState('');
    const [partners, setPartners] = useState(cachedData?.partners || []);
    const [companiesByPartner, setCompaniesByPartner] = useState(
        cachedData?.companiesByPartner || {},
    );
    const [expandedPartners, setExpandedPartners] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(!!cachedData);
    const [cacheKey, setCacheKey] = useState(
        cachedData ? `${activeWholesaler}` : '',
    );
    const [visibleCount, setVisibleCount] = useState(15); // Start with 15 to ensure scrollbar shows
    const searchInputRef = useRef(null);
    const scrollContainerRef = useRef(null);

    // Update states when cached data changes (wholesaler changes)
    useEffect(() => {
        if (cachedData) {
            setPartners(cachedData.partners || []);
            setCompaniesByPartner(cachedData.companiesByPartner || {});
            setCacheKey(
                activeScope === 40 && activePartnerId
                    ? `partner_${activePartnerId}`
                    : `wholesaler_${activeWholesaler}`,
            );
            setDataLoaded(true);
        }
    }, [cachedData, activeWholesaler, activeScope, activePartnerId]);

    const fetchData = useCallback(async () => {
        // Check if we already have data for this scope/context
        const newCacheKey =
            activeScope === 40 && activePartnerId
                ? `partner_${activePartnerId}`
                : `wholesaler_${activeWholesaler}`;
        if (dataLoaded && cacheKey === newCacheKey) {
            return; // Don't refetch if we already have data
        }

        setIsLoading(true);
        setError(null);
        try {
            // Use shared cache - returns processed data ready to use
            const processedData = await getNavigationData(
                activeWholesaler,
                activeScope,
                activePartnerId,
            );

            // Set state directly - data is already processed!
            setPartners(processedData.partners || []);
            setCompaniesByPartner(processedData.companiesByPartner || {});
            setDataLoaded(true);
            setCacheKey(newCacheKey);
        } catch (err) {
            console.error('[PartnerCompanyPicker] Failed to load data:', err);
            setError('Failed to load partners and companies');
        } finally {
            setIsLoading(false);
        }
    }, [activeWholesaler, activeScope, activePartnerId, dataLoaded, cacheKey]);

    // Pre-process data immediately when modal opens - data should already be cached by PartnerCompanyField
    useEffect(() => {
        if (isOpen && !dataLoaded) {
            // For scope 40 need partnerId, for scope 60+ need wholesalerId
            if (
                (activeScope === 40 && activePartnerId) ||
                (activeScope >= 60 && activeWholesaler)
            ) {
                fetchData();
            }
        }
    }, [
        isOpen,
        activeWholesaler,
        activeScope,
        activePartnerId,
        dataLoaded,
        fetchData,
    ]);

    // Reset cache when scope context changes
    useEffect(() => {
        const expectedCacheKey =
            activeScope === 40 && activePartnerId
                ? `partner_${activePartnerId}`
                : `wholesaler_${activeWholesaler}`;
        if (cacheKey && cacheKey !== expectedCacheKey) {
            setDataLoaded(false);
            setPartners([]);
            setCompaniesByPartner({});
        }
    }, [activeWholesaler, activeScope, activePartnerId, cacheKey]);

    // When modal opens, focus search and auto-expand current partner
    useEffect(() => {
        if (isOpen) {
            // Use setTimeout to avoid blocking the modal open
            setTimeout(() => searchInputRef.current?.focus(), 0);
            // Auto-expand current partner if set (so selected company is visible)
            const targetPartnerId = currentPartnerId || currentPartner;
            if (targetPartnerId) {
                setExpandedPartners(new Set([targetPartnerId]));
            } else {
                setExpandedPartners(new Set());
            }
        }
    }, [isOpen, currentPartnerId, currentPartner]);

    // Get company count for each partner (instant lookup, pre-computed)
    const getPartnerCompanyCount = (partnerId) => {
        return companiesByPartner[partnerId]?.length || 0;
    };

    // Filter and sort partners - ONLY when needed!
    const filteredPartners = useMemo(() => {
        if (!dataLoaded || partners.length === 0) {
            return [];
        }

        let filtered = partners;

        // No longer filter by partner - always show all partners in company modal
        // filterByPartnerId is DEPRECATED

        // Apply search filter ONLY if searching
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = partners.filter((partner) => {
                // Quick check: partner name match
                if (
                    partner.friendlyName?.toLowerCase().includes(lowerSearch) ||
                    partner.legalName?.toLowerCase().includes(lowerSearch)
                ) {
                    return true;
                }

                // Search companies including TenantID and Domain
                const partnerCompanies = companiesByPartner[partner.id] || [];
                return partnerCompanies.some(
                    (c) =>
                        c.friendlyName?.toLowerCase().includes(lowerSearch) ||
                        c.legalName?.toLowerCase().includes(lowerSearch) ||
                        c.tenantID?.toLowerCase().includes(lowerSearch) ||
                        c.initialMicrosoftDomain
                            ?.toLowerCase()
                            .includes(lowerSearch),
                );
            });
        }

        // Smart ordering: selected first, then managed (if different), then alphabetically
        // If managed partner is the partner company itself, disregard and show alphabetically
        const selectedId = currentPartnerId || currentPartner;
        const managedId = managedPartnerId;

        // Check if managed partner is the "partner company" (has no companies - it's the partner itself)
        const managedPartnerData = managedId
            ? filtered.find((p) => p.id === managedId)
            : null;
        const isManagedPartnerCompany =
            managedPartnerData &&
            (companiesByPartner[managedId]?.length === 0 ||
                !companiesByPartner[managedId]);

        // Build priority array
        const priorityIds = [];
        if (selectedId) priorityIds.push(selectedId);
        // Only add managed if different from selected and not a partner company
        if (managedId && managedId !== selectedId && !isManagedPartnerCompany) {
            priorityIds.push(managedId);
        }

        if (priorityIds.length > 0) {
            const priorityPartners = [];
            const restPartners = [];

            filtered.forEach((p) => {
                const priorityIndex = priorityIds.indexOf(p.id);
                if (priorityIndex !== -1) {
                    priorityPartners[priorityIndex] = p;
                } else {
                    restPartners.push(p);
                }
            });

            // Filter out undefined (in case priority partner not found)
            return [...priorityPartners.filter(Boolean), ...restPartners];
        }

        return filtered; // Already sorted from cache!
    }, [
        partners,
        searchTerm,
        currentPartnerId,
        currentPartner,
        companiesByPartner,
        dataLoaded,
        managedPartnerId,
    ]);

    // Filter companies within a partner - With smart ordering
    const getFilteredCompaniesForPartner = (partnerId) => {
        const partnerCompanies = companiesByPartner[partnerId] || [];

        // Helper to apply smart ordering to company list
        const applySmartOrdering = (companies) => {
            // Priority: selected company first, then managed company (if different), then alphabetically
            const priorityIds = [];
            if (currentCompanyId) priorityIds.push(currentCompanyId);
            // Only add managed if different from selected
            if (managedCompanyId && managedCompanyId !== currentCompanyId) {
                priorityIds.push(managedCompanyId);
            }

            if (priorityIds.length > 0) {
                const priorityCompanies = [];
                const restCompanies = [];

                companies.forEach((c) => {
                    const priorityIndex = priorityIds.indexOf(c.id);
                    if (priorityIndex !== -1) {
                        priorityCompanies[priorityIndex] = c;
                    } else {
                        restCompanies.push(c);
                    }
                });

                return [...priorityCompanies.filter(Boolean), ...restCompanies];
            }

            return companies;
        };

        // No search - apply smart ordering to cached list
        if (!searchTerm) {
            return applySmartOrdering(partnerCompanies);
        }

        // With search - filter including TenantID and Domain
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = partnerCompanies.filter(
            (company) =>
                company.friendlyName?.toLowerCase().includes(lowerSearch) ||
                company.legalName?.toLowerCase().includes(lowerSearch) ||
                company.tenantID?.toLowerCase().includes(lowerSearch) ||
                company.initialMicrosoftDomain
                    ?.toLowerCase()
                    .includes(lowerSearch),
        );

        // Apply smart ordering to filtered results
        return applySmartOrdering(filtered);
    };

    // Toggle partner expansion
    const togglePartner = (partnerId) => {
        const newExpanded = new Set(expandedPartners);
        if (newExpanded.has(partnerId)) {
            newExpanded.delete(partnerId);
        } else {
            newExpanded.add(partnerId);
        }
        setExpandedPartners(newExpanded);
    };

    // Handle scroll to load more partners (infinite scroll)
    const handleScroll = useCallback(
        (e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            // Load more when scrolled to 80% of content
            if (scrollHeight - scrollTop <= clientHeight * 1.2) {
                if (visibleCount < filteredPartners.length) {
                    setVisibleCount((prev) =>
                        Math.min(prev + 30, filteredPartners.length),
                    );
                }
            }
        },
        [visibleCount, filteredPartners.length],
    );

    // Reset visible count when search changes or show all if searching
    useEffect(() => {
        // When searching, show all results immediately
        // When not searching, start with 15 for performance while ensuring scrollbar shows
        setVisibleCount(searchTerm ? filteredPartners.length : 15);
    }, [searchTerm, filteredPartners.length]);

    // Handle partner selection
    const handlePartnerSelect = (partner) => {
        onSelect({
            partnerId: partner.id,
            partnerName: partner.friendlyName || partner.legalName,
            companyId: null,
            companyName: null,
            companyDomain: '',
            companyTenantID: '',
        });
        onClose();
    };

    // Handle company selection
    const handleCompanySelect = (company, partner) => {
        onSelect({
            partnerId: partner.id,
            partnerName:
                partner.friendlyName || partner.legalName || partner.name,
            companyId: company.id,
            companyName:
                company.friendlyName || company.legalName || company.name,
            companyDomain: company.initialMicrosoftDomain || '',
            companyTenantID: company.tenantID || '',
            companyIsTrial: company.isTrial === true && !company.trialHold,
        });
        onClose();
    };

    // Pill styles matching numbers page StatusCellRenderer
    const pillStyles = {
        base: {
            borderRadius: '100px',
            paddingLeft: '8px',
            paddingRight: '12px',
            lineHeight: '1.8',
            fontWeight: '500',
            height: '22px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '11px',
            flexShrink: 0,
            marginLeft: '8px',
        },
        notApproved: {
            backgroundColor: 'rgb(255, 0, 0, 0.05)',
            border: '1.5px solid #d64759',
            color: '#d64759',
        },
        disabled: {
            backgroundColor: '#79797936',
            border: '1.5px solid #929292',
            color: '#666666',
        },
        trial: {
            backgroundColor: '#88d9ff38',
            border: '1.5px solid #0084ff',
            color: '#0084ff',
        },
    };

    // Get status badge for company (pill-style matching numbers page)
    const getStatusBadge = (company) => {
        // Check approval status first
        if (company.approved === false) {
            return (
                <Box sx={{ ...pillStyles.base, ...pillStyles.notApproved }}>
                    Not Approved
                </Box>
            );
        }
        // Check if disabled/inactive
        if (company.isActive === false) {
            return (
                <Box sx={{ ...pillStyles.base, ...pillStyles.disabled }}>
                    Disabled
                </Box>
            );
        }
        // Check trial status
        if (company.isTrial === true && !company.trialHold) {
            return (
                <Box sx={{ ...pillStyles.base, ...pillStyles.trial }}>
                    Trial
                </Box>
            );
        }
        // Fallback to legacy status field if new fields not available
        if (company.status === 'Trial') {
            return (
                <Box sx={{ ...pillStyles.base, ...pillStyles.trial }}>
                    Trial
                </Box>
            );
        }
        if (company.status === 'Disabled') {
            return (
                <Box sx={{ ...pillStyles.base, ...pillStyles.disabled }}>
                    Disabled
                </Box>
            );
        }
        if (company.status === 'Not Approved') {
            return (
                <Box sx={{ ...pillStyles.base, ...pillStyles.notApproved }}>
                    Not Approved
                </Box>
            );
        }
        return null;
    };

    // Check if company is clickable (not approved or not active companies are unclickable)
    const isCompanyClickable = (company) => {
        // New fields take priority
        if (company.approved === false || company.isActive === false) {
            return false;
        }
        // Fallback to legacy status field
        return (
            company.status !== 'Disabled' && company.status !== 'Not Approved'
        );
    };

    // Truncate text with ellipsis
    const truncateText = (text, maxLength = 50) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength - 3) + '...';
    };

    // Get colors from palette
    const modalColors = getModalColors(darkMode);
    const partnerColors = getPickerColors(darkMode, 'partner');
    const companyColors = getPickerColors(darkMode, 'company');
    const disabledBg = darkMode ? '#171923' : '#EDF2F7';

    // Custom scrollbar styles
    const scrollbarStyles = {
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: darkMode ? '#2D3748' : '#F1F1F1',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: darkMode ? '#4A5568' : '#C1C1C1',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: darkMode ? '#718096' : '#A1A1A1',
        },
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            closeOnOverlayClick={true}>
            <ModalOverlay />
            <ModalContent bg={modalColors.background} maxW="550px">
                <ModalHeader
                    borderBottom={`1px solid ${modalColors.headerBorder}`}
                    py={3}>
                    <HStack justify="space-between" align="center">
                        <Text fontSize="md">{title}</Text>
                        <IconButton
                            aria-label="Close"
                            icon={<CloseIcon boxSize={3} />}
                            size="sm"
                            variant="ghost"
                            onClick={onClose}
                            color="black"
                            _hover={{ bg: darkMode ? '#4A5568' : 'gray.200' }}
                        />
                    </HStack>
                </ModalHeader>
                <ModalBody p={3}>
                    {/* Search Box - at top */}
                    <InputGroup mb={3} size="sm">
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="white" boxSize={3} />
                        </InputLeftElement>
                        <Input
                            ref={searchInputRef}
                            placeholder="Search Partner or Company..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            bg={modalColors.searchBackground}
                            borderColor={modalColors.searchBorder}
                            color={darkMode ? 'white' : 'black'}
                            _placeholder={{
                                color: darkMode ? 'gray.400' : 'gray.500',
                            }}
                            _focus={{
                                borderColor: partnerColors.selectedBorder,
                                boxShadow: `0 0 0 1px ${partnerColors.selectedBorder}`,
                            }}
                        />
                        {searchTerm && (
                            <InputRightElement width="auto" pr={2}>
                                <IconButton
                                    aria-label="Clear search"
                                    icon={<CloseIcon boxSize={2} />}
                                    size="xs"
                                    variant="ghost"
                                    onClick={() => setSearchTerm('')}
                                    color="black"
                                />
                            </InputRightElement>
                        )}
                    </InputGroup>

                    {/* No Wholesaler Selected State */}
                    {activeScope >= 60 && !activeWholesaler && (
                        <Box textAlign="center" py={8} color="gray.500">
                            <Text>Please select a wholesaler first</Text>
                        </Box>
                    )}

                    {/* Loading State */}
                    {isLoading && !dataLoaded && activeWholesaler && (
                        <Box textAlign="center" py={8}>
                            <Text>Loading...</Text>
                        </Box>
                    )}

                    {/* Error State */}
                    {error && (
                        <Box color="red.500" py={4}>
                            <Text>{error}</Text>
                        </Box>
                    )}

                    {/* Partners and Companies List */}
                    {!error && dataLoaded && (
                        <VStack
                            align="stretch"
                            spacing={1}
                            maxH="350px"
                            overflowY="auto"
                            pr={1}
                            ref={scrollContainerRef}
                            onScroll={handleScroll}
                            sx={scrollbarStyles}>
                            {/* Partner Items - Virtual Scrolling (only when not searching) */}
                            {(searchTerm
                                ? filteredPartners
                                : filteredPartners.slice(0, visibleCount)
                            ).map((partner) => {
                                const isExpanded = expandedPartners.has(
                                    partner.id,
                                );
                                const partnerCompanies =
                                    getFilteredCompaniesForPartner(partner.id);
                                const companyCount = getPartnerCompanyCount(
                                    partner.id,
                                );
                                const displayName = truncateText(
                                    partner.friendlyName || partner.legalName,
                                    45,
                                );
                                const isSelectedPartner =
                                    partner.id ===
                                    (currentPartnerId || currentPartner);

                                // Partner tooltip - show legal name if different
                                const partnerTooltip =
                                    partner.legalName &&
                                    partner.legalName !== partner.friendlyName
                                        ? `Legal: ${partner.legalName}`
                                        : null;

                                return (
                                    <Box key={partner.id}>
                                        {/* Partner Row */}
                                        <Tooltip
                                            label={partnerTooltip}
                                            placement="top"
                                            hasArrow
                                            isDisabled={!partnerTooltip}>
                                            <Box
                                                p={2}
                                                bg={
                                                    isSelectedPartner
                                                        ? partnerColors.selectedBackground
                                                        : partnerColors.background
                                                }
                                                borderRadius="md"
                                                cursor="pointer"
                                                borderWidth="1px"
                                                borderColor={
                                                    isSelectedPartner
                                                        ? partnerColors.selectedBorder
                                                        : partnerColors.border
                                                }
                                                _hover={{
                                                    bg: isSelectedPartner
                                                        ? partnerColors.selectedHoverBackground
                                                        : partnerColors.hoverBackground,
                                                    borderColor:
                                                        isSelectedPartner
                                                            ? partnerColors.selectedHoverBorder
                                                            : partnerColors.hoverBorder,
                                                }}
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center">
                                                <Box
                                                    flex={1}
                                                    onClick={() =>
                                                        handlePartnerSelect(
                                                            partner,
                                                        )
                                                    }>
                                                    <HStack
                                                        justify="space-between"
                                                        align="center">
                                                        <Text
                                                            fontWeight="medium"
                                                            color={
                                                                partnerColors.text
                                                            }
                                                            fontSize="sm"
                                                            overflow="hidden"
                                                            textOverflow="ellipsis"
                                                            whiteSpace="nowrap">
                                                            {displayName}
                                                        </Text>
                                                        <Text
                                                            fontSize="xs"
                                                            color={
                                                                partnerColors.text
                                                            }
                                                            ml={2}
                                                            mr={4}
                                                            flexShrink={0}
                                                            opacity={0.8}>
                                                            {companyCount}{' '}
                                                            {companyCount === 1
                                                                ? 'Company'
                                                                : 'Companies'}
                                                        </Text>
                                                    </HStack>
                                                </Box>
                                                <IconButton
                                                    size="sm"
                                                    variant="ghost"
                                                    icon={
                                                        isExpanded ? (
                                                            <ChevronDownIcon
                                                                boxSize={5}
                                                            />
                                                        ) : (
                                                            <ChevronRightIcon
                                                                boxSize={5}
                                                            />
                                                        )
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        togglePartner(
                                                            partner.id,
                                                        );
                                                    }}
                                                    aria-label="Toggle companies"
                                                    color={partnerColors.text}
                                                    bg="transparent"
                                                    _hover={{
                                                        bg: partnerColors.hoverBackground,
                                                    }}
                                                    ml={1}
                                                />
                                            </Box>
                                        </Tooltip>

                                        {/* Companies under this partner - Only render when expanded */}
                                        {isExpanded && (
                                            <Box pt={1} pl={3}>
                                                {partnerCompanies.length ===
                                                0 ? (
                                                    <Text
                                                        fontSize="xs"
                                                        color="gray.500"
                                                        p={2}>
                                                        No companies found
                                                    </Text>
                                                ) : (
                                                    partnerCompanies.map(
                                                        (company) => {
                                                            const clickable =
                                                                isCompanyClickable(
                                                                    company,
                                                                );
                                                            const companyDisplayName =
                                                                truncateText(
                                                                    company.friendlyName ||
                                                                        company.legalName,
                                                                    55,
                                                                );
                                                            const isSelectedCompany =
                                                                company.id ===
                                                                currentCompanyId;

                                                            // Build tooltip with Domain, TenantID, Status only
                                                            const tooltipLines =
                                                                [];
                                                            if (
                                                                company.initialMicrosoftDomain
                                                            )
                                                                tooltipLines.push(
                                                                    `Domain: ${company.initialMicrosoftDomain}`,
                                                                );
                                                            if (
                                                                company.tenantID
                                                            )
                                                                tooltipLines.push(
                                                                    `TenantID: ${company.tenantID}`,
                                                                );
                                                            // Add status info
                                                            if (
                                                                company.approved ===
                                                                false
                                                            )
                                                                tooltipLines.push(
                                                                    'Status: Not Approved',
                                                                );
                                                            else if (
                                                                company.isActive ===
                                                                false
                                                            )
                                                                tooltipLines.push(
                                                                    'Status: Disabled',
                                                                );
                                                            else if (
                                                                company.isTrial ===
                                                                    true &&
                                                                !company.trialHold
                                                            )
                                                                tooltipLines.push(
                                                                    'Status: Trial',
                                                                );
                                                            else
                                                                tooltipLines.push(
                                                                    'Status: Active',
                                                                );
                                                            const tooltipLabel =
                                                                tooltipLines.length >
                                                                0
                                                                    ? tooltipLines.join(
                                                                          '\n',
                                                                      )
                                                                    : null;

                                                            // Determine background/border colors based on state
                                                            let bgColor =
                                                                companyColors.background;
                                                            let borderColorVal =
                                                                companyColors.border;
                                                            let hoverBg =
                                                                companyColors.hoverBackground;
                                                            let hoverBorder =
                                                                companyColors.hoverBorder;

                                                            if (
                                                                isSelectedCompany
                                                            ) {
                                                                bgColor =
                                                                    companyColors.selectedBackground;
                                                                borderColorVal =
                                                                    companyColors.selectedBorder;
                                                                hoverBg =
                                                                    companyColors.selectedHoverBackground;
                                                                hoverBorder =
                                                                    companyColors.selectedHoverBorder;
                                                            } else if (
                                                                !clickable
                                                            ) {
                                                                bgColor =
                                                                    disabledBg;
                                                            }

                                                            return (
                                                                <Tooltip
                                                                    key={
                                                                        company.id
                                                                    }
                                                                    label={
                                                                        tooltipLabel
                                                                    }
                                                                    placement="right"
                                                                    hasArrow
                                                                    isDisabled={
                                                                        !tooltipLabel
                                                                    }
                                                                    whiteSpace="pre-wrap">
                                                                    <Box
                                                                        p={2}
                                                                        mb={1}
                                                                        bg={
                                                                            bgColor
                                                                        }
                                                                        borderRadius="md"
                                                                        borderWidth="1px"
                                                                        borderColor={
                                                                            borderColorVal
                                                                        }
                                                                        cursor={
                                                                            clickable
                                                                                ? 'pointer'
                                                                                : 'not-allowed'
                                                                        }
                                                                        opacity={
                                                                            clickable
                                                                                ? 1
                                                                                : 0.6
                                                                        }
                                                                        _hover={
                                                                            clickable
                                                                                ? {
                                                                                      bg: hoverBg,
                                                                                      borderColor:
                                                                                          hoverBorder,
                                                                                  }
                                                                                : {}
                                                                        }
                                                                        onClick={() =>
                                                                            clickable &&
                                                                            handleCompanySelect(
                                                                                company,
                                                                                partner,
                                                                            )
                                                                        }>
                                                                        <HStack
                                                                            justify="space-between"
                                                                            align="center">
                                                                            <Text
                                                                                fontSize="sm"
                                                                                color={
                                                                                    companyColors.text
                                                                                }
                                                                                fontWeight={
                                                                                    isSelectedCompany
                                                                                        ? 'medium'
                                                                                        : 'normal'
                                                                                }
                                                                                overflow="hidden"
                                                                                textOverflow="ellipsis"
                                                                                whiteSpace="nowrap"
                                                                                flex={
                                                                                    1
                                                                                }>
                                                                                {
                                                                                    companyDisplayName
                                                                                }
                                                                            </Text>
                                                                            {getStatusBadge(
                                                                                company,
                                                                            )}
                                                                        </HStack>
                                                                    </Box>
                                                                </Tooltip>
                                                            );
                                                        },
                                                    )
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}

                            {/* Show "Load More" hint - only when not searching */}
                            {!searchTerm &&
                                visibleCount < filteredPartners.length && (
                                    <Box textAlign="center" py={2}>
                                        <Text color="gray.500" fontSize="sm">
                                            Showing {visibleCount} of{' '}
                                            {filteredPartners.length} partners.
                                            Scroll for more...
                                        </Text>
                                    </Box>
                                )}

                            {filteredPartners.length === 0 && (
                                <Box textAlign="center" py={8}>
                                    <Text color="gray.500">
                                        No partners or companies found
                                    </Text>
                                </Box>
                            )}
                        </VStack>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default PartnerCompanyPicker;
